import {
	AUTH_BRODCAST_CHANNEL_KEY,
	AuthBroadcastChannelMessage,
	AuthBroadcastMessageState
} from './types'

let authChannel: BroadcastChannel | null = null

/**
 * Provides a singleton instance of a BroadcastChannel for auth-related messages.
 * Ensures a single channel instance is shared across the application.
 *
 * @returns {BroadcastChannel} The shared BroadcastChannel instance.
 */
const getAuthBroadcastChannel = (): BroadcastChannel => {
	if (!authChannel) {
		authChannel = new BroadcastChannel(AUTH_BRODCAST_CHANNEL_KEY)
	}
	return authChannel
}

/**
 * Sends a one-time broadcast message through the auth-related BroadcastChannel.
 *
 * @param {AuthBroadcastChannelMessage} message - The message payload to broadcast.
 */
const sendAuthBroadcastChannelMessage = (
	message: AuthBroadcastChannelMessage
) => {
	const channel = getAuthBroadcastChannel()

	// Send the message through the channel
	channel.postMessage(message)
}

export const handleBeforeUnload = (event: BeforeUnloadEvent) => {
	event.preventDefault()
	event.returnValue = ''
}

/**
 * Sets up a listener for auth-related messages on the BroadcastChannel.
 * If a message with type {@link AuthBroadcastMessageState.signIn} is received,
 * the page is reloaded automatically.
 *
 * This method ensures cleanup happens automatically as the browser handles
 * the lifecycle of event listeners on the BroadcastChannel.
 */
export const listenForAuthBroadcastChannelMessage = () => {
	const channel = getAuthBroadcastChannel()

	/**
	 * Handles incoming messages on the BroadcastChannel.
	 * Reloads the page if a success or sign-out message is received.
	 *
	 * @param {MessageEvent} event - The event containing the broadcast message.
	 */
	const handleMessage = (event: MessageEvent) => {
		if (event.data?.type === AuthBroadcastMessageState.signIn) {
			// Replace and reload the page on success (Sign In)
			window.location.replace('/')
		} else if (event.data?.type === AuthBroadcastMessageState.signOut) {
			window.removeEventListener('beforeunload', handleBeforeUnload)

			// Replace and reload the page on Sign Out
			window.location.replace('/')
		}
	}

	channel.addEventListener('message', handleMessage)
}

/**
 * Sends a broadcast message when the user signs in successfully.
 * This ensures that all open tabs receive the message and refresh.
 * After the reload the user will be redirected to the home.
 */
export const broadcastSuccessSignIn = () => {
	sendAuthBroadcastChannelMessage({
		type: AuthBroadcastMessageState.signIn
	})
}

/**
 * Sends a broadcast message when the user logs out.
 * This ensures that all open tabs receive the message and refresh.
 * After the reload the user will be redirected to the login.
 */
export const broadcastSignOut = () => {
	sendAuthBroadcastChannelMessage({
		type: AuthBroadcastMessageState.signOut
	})
}
